import Vue from "vue";
import ApiService from "../../services/ApiService";
import { EventBus, EVENTS } from "../../services/EventBus";

Vue.component("fan-badge-display", {
  delimiters: ["${", "}"],

  props: {
    position: {
      type: String,
      default: "bottom-left", // bottom-left, top-right, profile, inline
      validator: (value) => ["bottom-left", "top-right", "profile", "inline"].includes(value)
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      badgeInfo: null,
      isLoading: true,
      isLoggedIn: true
    };
  },

  computed: {
    currentBadgeTitle() {
      if (!this.badgeInfo || !this.badgeInfo.currentBadge) {
        return "Fanliebling-Fan";
      }
      return this.badgeInfo.currentBadge.title;
    },

    currentBadgeColor() {
      if (!this.badgeInfo || !this.badgeInfo.currentBadge) {
        return "#8B7355";
      }
      return this.badgeInfo.currentBadge.color;
    },

    interactionCount() {
      return this.badgeInfo ? this.badgeInfo.interactionCount : 0;
    },

    progressPercentage() {
      if (!this.badgeInfo || !this.badgeInfo.progress) {
        return 0;
      }
      return this.badgeInfo.progress.percentage;
    },

    nextBadgeTitle() {
      if (!this.badgeInfo || !this.badgeInfo.nextBadge) {
        return "";
      }
      return this.badgeInfo.nextBadge.title;
    },

    hasNextLevel() {
      return this.badgeInfo && this.badgeInfo.hasNextLevel;
    },

    positionClass() {
      return `badge-position-${this.position}`;
    },

    // CSS Custom Properties für Badge-Farben (Dark/Light Mode kompatibel)
    badgeColorStyle() {
      const color = this.currentBadgeColor;
      // Bestimme welche vordefinierte CSS-Variable verwendet wird
      const cssVarMap = {
        "#8B7355": "--badge-color-fan",       // Fanliebling-Fan
        "#CD7F32": "--badge-color-insider",   // Fan-Insider
        "#C0C0C0": "--badge-color-beobachter", // Spielbeobachter
        "#FFD700": "--badge-color-leader"     // Tribünen-Leader
      };
      
      const cssVar = cssVarMap[color] || "--badge-color-fan";
      
      return {
        "--badge-current-color": `var(${cssVar})`,
        "--badge-color-bg": `var(${cssVar})`,
        "--badge-color-border": `var(${cssVar})`
      };
    }
  },

  created() {    
  },

  mounted() {
    // Only load in browser, not during SSR
    if (typeof window !== "undefined") {
      this.loadBadgeInfo();
      
      // Event Listener für Interaktionen registrieren
      EventBus.$on(EVENTS.INTERACTION_COMPLETED, this.handleInteractionCompleted);
    }
  },

  beforeDestroy() {
    // Event Listener aufräumen
    EventBus.$off(EVENTS.INTERACTION_COMPLETED, this.handleInteractionCompleted);
  },

  methods: {
    handleInteractionCompleted() {
      // Badge-Info neu laden nach Interaktion
      this.loadBadgeInfo();
    },

    loadBadgeInfo() {
      this.isLoading = true;
      
      ApiService.get("/rest/fanliebling/badge/user")
        .done((response) => {          
          // ApiService gibt das data-Objekt direkt zurück
          if (response && response.isLoggedIn === false) {
            // User ist nicht eingeloggt - zeige Platzhalter            
            this.isLoggedIn = false;
            this.badgeInfo = null;
          } else if (response && response.interactionCount !== undefined) {
            this.isLoggedIn = true;
            this.badgeInfo = response;            
          }
          this.isLoading = false;
        })
        .fail((error) => {
          console.error("Error loading badge info:", error);
          this.isLoading = false;
        });
    },

    openLoginModal() {
      // Öffne das Login Modal
      const loginButton = document.querySelector('.control-user a[data-toggle="modal"]');
      if (loginButton) {
        loginButton.click();
      }
    }
  },

  render: function(h) {
    if (this.isLoading) {
      return h("div", { class: `fan-badge-display ${this.positionClass}` }, [
        h("div", { class: "text-center" }, [
          h("i", { class: "fa fa-spinner fa-spin fa-2x" })
        ])
      ]);
    }

    // Platzhalter für nicht-eingeloggte User
    if (!this.isLoggedIn) {
      return h("div", { 
        class: `fan-badge-display ${this.positionClass} badge-placeholder`,
        on: {
          click: this.openLoginModal
        },
        style: {
          cursor: "pointer"
        }
      }, [
        h("div", { class: "badge-content" }, [
          h("div", { class: "badge-placeholder-main" }, [
            h("div", { class: "badge-icon" }, [
              h("i", { 
                class: "fa fa-star fa-2x",
                style: { color: "#ccc" }
              })
            ]),
            h("div", { class: "badge-text" }, [
              h("h6", { class: "badge-title mb-1" }, "Dein Fan-Status"),
              h("p", { class: "small mb-0" }, "Sammle Interaktionen und erreiche exklusive Level!")
            ])
          ]),
          h("button", { 
            class: "btn btn-primary btn-sm mt-2 w-100",
            on: {
              click: (e) => {
                e.stopPropagation();
                this.openLoginModal();
              }
            }
          }, [
            h("i", { class: "fa fa-sign-in mr-1" }),
            "Jetzt anmelden"
          ])
        ])
      ]);
    }

    if (!this.badgeInfo) {
      return null;
    }

    const badgeContent = [];

    // Main Badge Container (Icon + Title + Count)
    const mainContent = [];

    // Badge Icon/Star
    mainContent.push(
      h("div", { class: "badge-icon" }, [
        h("i", { 
          class: "fa fa-star fa-2x",
          style: { color: "var(--badge-color-bg)" }
        })
      ])
    );

    // Badge Info (Title + Count)
    const badgeInfoContent = [];

    // Badge Title
    if (!this.compact) {
      badgeInfoContent.push(
        h("h6", { 
          class: "badge-title mb-1",
          style: { color: "var(--badge-color-bg)" }
        }, this.currentBadgeTitle)
      );
    }

    // Interaction Count
    badgeInfoContent.push(
      h("p", { class: "badge-interactions mb-0" }, [
        h("strong", {}, this.interactionCount),
        " Interaktionen"
      ])
    );

    mainContent.push(
      h("div", { class: "badge-text" }, badgeInfoContent)
    );

    badgeContent.push(
      h("div", { class: "badge-main" }, mainContent)
    );

    // Progress Bar
    if (this.showProgress && this.hasNextLevel) {
      badgeContent.push(
        h("div", { class: "progress-container mt-2" }, [
          h("div", { class: "progress" }, [
            h("div", {
              class: "progress-bar",
              style: {
                width: `${this.progressPercentage}%`,
                backgroundColor: "var(--badge-color-bg)"
              },
              attrs: {
                role: "progressbar",
                "aria-valuenow": this.progressPercentage,
                "aria-valuemin": "0",
                "aria-valuemax": "100"
              }
            })
          ]),
          h("small", { class: "mt-1 d-block" }, 
            `${this.progressPercentage}% bis ${this.nextBadgeTitle}`
          )
        ])
      );
    }

    return h("div", { 
      class: `fan-badge-display ${this.positionClass}`,
      style: this.badgeColorStyle 
    }, [
      h("div", { class: "badge-content" }, badgeContent)
    ]);
  }
});
